'use client';

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimation from './animations/scroll-lines-animation';
import clsx from 'clsx';
import {
    buildStaticAssetPath,
    getStaticAssetUrl,
} from '@/app/lib/utils/static-asset-url';

function SimulationDisadvantages({ locale }: { locale: string }) {
    const { t } = useTranslation('simulation-disadvantages');

    const rawCards = t('cards', { returnObjects: true });
    const cardList: { id: string; title: string; description?: string }[] =
        Array.isArray(rawCards) ? rawCards : [];
    const tabletBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('home', 'bg-simulation-disadvantages-t.webp'),
    );
    const desktopBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('home', 'bg-simulation-disadvantages.webp'),
    );

    const rootRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const middleContainerRef = useRef<HTMLDivElement>(null);
    const scrollContentRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            return initAnimation(
                rootRef.current!,
                titleRef.current!,
                middleContainerRef.current!,
                scrollContentRef.current!,
            );
        },
        { scope: rootRef },
    );

    const simTypes = t('simTypes', { returnObjects: true }) as string[][];
    return (
        <section
            ref={rootRef}
            className="relative -mb-px flex w-full flex-col items-center overflow-hidden bg-[#171D2C]"
        >
            <div
                className="tablet:block desktop:hidden pointer-events-none absolute inset-0 hidden bg-top bg-no-repeat"
                style={{
                    backgroundSize: '100% auto',
                    backgroundImage: `url(${tabletBackgroundImageUrl})`,
                }}
            />
            <div
                className="desktop:block pointer-events-none absolute inset-0 hidden bg-top bg-no-repeat"
                style={{
                    backgroundSize: '100% auto',
                    backgroundImage: `url(${desktopBackgroundImageUrl})`,
                }}
            />
            {/* 顶部标题区 —— 视觉上固定在顶部（跟随 section pin） */}
            <div
                ref={titleRef}
                className="j-h4 tablet:j-h2 tablet:items-center tablet:text-center tablet:pt-[120px] desktop:pt-[160px] tablet:pb-[24px] tablet:max-w-[85.5vw] flex w-[85.5vw] max-w-[480px] flex-col items-start pb-[16px] pt-[80px] text-start"
            >
                {(t('title', { returnObjects: true }) as string[]).map(
                    (line, idx) => (
                        <p key={idx} className="text-white">
                            {line.split(/\*\*(.*?)\*\*/g).map((part, i) =>
                                i % 2 === 1 ? (
                                    <span
                                        key={i}
                                        className="bg-[linear-gradient(90deg,#5C76FF_0%,#AD5CFF_93.24%)] bg-clip-text text-transparent"
                                    >
                                        {part}
                                    </span>
                                ) : (
                                    part
                                ),
                            )}
                        </p>
                    ),
                )}
            </div>

            {/* 中间滚动区 —— overflow hidden 窗口 */}
            <div
                ref={middleContainerRef}
                className="tablet:w-[500px] desktop:w-[600px] tablet:h-[470px] desktop:h-[500px] tablet:max-w-[500px] desktop:max-w-[600px] relative h-[440px] w-[85.5vw] max-w-[480px] overflow-hidden"
                style={{
                    maskImage:
                        'linear-gradient(to bottom, transparent, #171D2C 20%, #171D2C 80%, transparent)',
                    WebkitMaskImage:
                        'linear-gradient(to bottom, transparent, #171D2C 20%, #171D2C 80%, transparent)',
                }}
            >
                {/* 实际内容，由 GSAP 驱动向上移动 */}
                <div
                    ref={scrollContentRef}
                    className="tablet:gap-[120px] desktop:gap-[160px] flex w-full flex-col gap-[80px]"
                >
                    {/* SimTypes 列表：每组是字符串数组，每行统一渲染，**xxx** 高亮 */}
                    {simTypes.map((group, i) => (
                        <div
                            key={i}
                            className={clsx(
                                `tablet:items-center flex w-full flex-col items-start text-center`,
                            )}
                        >
                            {i === 0 && locale !== 'zh' && (
                                <p className="j-h7 tablet:text-center w-full pb-[12px] text-start text-[#FFBF84] underline underline-offset-[4px]">
                                    Traditional Simulation
                                </p>
                            )}
                            {i === 1 && locale !== 'zh' && (
                                <p className="j-h7 tablet:text-center w-full pb-[12px] text-start text-[#FFBF84] underline underline-offset-[4px]">
                                    Generative AI-Enhanced Simulation
                                </p>
                            )}
                            {group.map((line, j) => (
                                <p
                                    key={j}
                                    className={clsx(
                                        'j-t3 tablet:text-center w-full text-start text-[#FFFFFF]',
                                        {
                                            'tablet:pb-[16px] pb-[12px]':
                                                j === 1 &&
                                                i <= 1 &&
                                                locale === 'zh',
                                            '!j-h7':
                                                j <= 1 &&
                                                locale === 'zh' &&
                                                i < 2,
                                        },
                                    )}
                                >
                                    {line
                                        .split(/\*\*(.*?)\*\*/g)
                                        .map((part, k) =>
                                            k % 2 === 1 ? (
                                                <span
                                                    key={k}
                                                    className="text-[#FFBF84]"
                                                >
                                                    {part}
                                                </span>
                                            ) : (
                                                part
                                            ),
                                        )}
                                </p>
                            ))}

                            {i === simTypes.length - 1 && (
                                <>
                                    <br />
                                    <br />
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div
                className={clsx(
                    'tablet:w-[87.5vw] tablet:max-w-[87.5vw] desktop:max-w-[1200px] w-[85.5vw] max-w-[480px]',
                    'tablet:gap-[48px] desktop:gap-[60px] flex flex-col gap-[32px]',
                    'tablet:pt-[100px] desktop:pt-[120px] pt-[80px]',
                    'tablet:pb-[120px] desktop:pb-[160px] pb-[80px]',
                )}
            >
                {/* 结论 */}
                <div className="j-h4 tablet:j-h2 tablet:items-center flex w-full flex-col items-start">
                    <p className="tablet:text-center whitespace-pre-line text-start text-white">
                        {t('conclusion')
                            .split(/\*\*(.*?)\*\*/g)
                            .map((part, i) =>
                                i % 2 === 1 ? (
                                    <span
                                        key={i}
                                        className="bg-[linear-gradient(90deg,#5C76FF_0%,#AD5CFF_93.24%)] bg-clip-text text-transparent"
                                    >
                                        {part}
                                    </span>
                                ) : (
                                    part
                                ),
                            )}
                    </p>
                </div>

                {/* 6个卡片：subgrid 等高标题 */}
                <div
                    className="tablet:grid-cols-2
                tablet:[grid-auto-rows:auto] tablet:gap-y-3
                desktop:grid-cols-3 desktop:[grid-auto-rows:auto] desktop:gap-y-3
                tablet:gap-x-[40px] desktop:gap-x-[60px] tablet:gap-y-[48px]
                desktop:gap-y-[80px] grid w-full
                grid-cols-1 gap-x-[0px] gap-y-[32px]
            "
                >
                    {cardList.map((item, index) => {
                        return (
                            <div
                                key={item.id}
                                className="
                                tablet:gap-[16px] tablet:row-span-2 tablet:grid tablet:[grid-template-rows:subgrid]
                                tablet:mb-0 desktop:row-span-2 desktop:grid desktop:[grid-template-rows:subgrid]
                                desktop:mb-0 flex flex-col gap-[8px]
                            "
                            >
                                <p className="j-h6 self-start bg-[linear-gradient(90deg,#8F7EFF_0%,#FFF_43.75%)] bg-clip-text text-transparent">
                                    {item.title}
                                </p>
                                {item.description && (
                                    <p
                                        className={`j-t2 text-justify text-[#CCCBCE]`}
                                    >
                                        {item.description
                                            .split(/\*\*(.*?)\*\*/g)
                                            .map((part, i) =>
                                                i % 2 === 1 ? (
                                                    <span
                                                        key={i}
                                                        className="text-[#FFBC7D]"
                                                    >
                                                        {part}
                                                    </span>
                                                ) : (
                                                    part
                                                ),
                                            )}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default SimulationDisadvantages;
